/* JIE JIANG jj-motion.js - Variant A motion system ported from draft_v2a (reveal.js + hero.js), vanilla JS only.
   - IntersectionObserver reveals (threshold .2, once), child stagger indices, section curtains (.jj-panel),
     count-up numbers, sticky-story parallax (.jj-story-sticky).
   - Hero carousel ([data-jj-hero], all widths): frames auto-advance on a timer and prev/next arrows switch
     manually — scroll never changes the frame (owner decision 2026-09-07, replaced the scroll-scrub model).
     The jade stone morphs between frame shapes (uniforms on the live canvas via window.JJ.jade); frames whose
     series has products swap in a product-photo layer (.jj-hero-photo). The gold rail tick marks the frame index.
   - prefers-reduced-motion: no autoplay; static active frame with the series names listed
     (CSS: .jj-hero.is-static), arrows and dots still navigate. No external requests, no libraries.
   All hidden "before" states in CSS are gated on html.jj-js (added here), so content is fully visible without JS. */
(function () {
  'use strict';
  var html = document.documentElement;
  html.classList.add('jj-js');
  var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Horizon scrolls the page inside div.page-wrapper (overflow:auto); element scroll events do not
     bubble to window, so scroll-driven work must listen on both (draft_v2a scrolled the window). */
  function scrollHost() { return document.querySelector('.page-wrapper'); }
  function bindScroll(fn) {
    window.addEventListener('scroll', fn, { passive: true });
    var sh = scrollHost();
    if (sh) sh.addEventListener('scroll', fn, { passive: true });
  }
  function unbindScroll(fn) {
    window.removeEventListener('scroll', fn);
    var sh = scrollHost();
    if (sh) sh.removeEventListener('scroll', fn);
  }

  /* ---------------- reveals ---------------- */
  function initReveals() {
    var els = [].slice.call(
      document.querySelectorAll('.jj-reveal,.jj-reveal-group,.jj-divider,.jj-panel,.jj-reveal-wipe')
    );

    document.querySelectorAll('.jj-reveal-group').forEach(function (g) {
      [].slice.call(g.children).forEach(function (c, i) { c.style.setProperty('--i', i); });
    });

    /* count-up numbers ("7 - 4 - 1"), tabular-nums so no layout shift */
    function countUp(el) {
      var n = parseInt(el.getAttribute('data-jj-count'), 10) || 0;
      if (reduce || n < 2) { el.textContent = String(n); return; }
      var t0 = performance.now(), D = 900;
      (function step(now) {
        var t = Math.min(1, (now - t0) / D);
        t = 1 - Math.pow(1 - t, 3);
        el.textContent = String(Math.round(n * t));
        if (t < 1) requestAnimationFrame(step);
      })(t0);
    }

    function arrive(el) {
      el.classList.add('is-in');
      el.querySelectorAll('[data-jj-count]').forEach(countUp);
    }
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(arrive);
      document.querySelectorAll('[data-jj-count]').forEach(function (el) {
        el.textContent = el.getAttribute('data-jj-count');
      });
      return;
    }
    /* a clip-path'd element (.jj-reveal-wipe) never "intersects" in Chrome - observe its unclipped parent */
    var byProxy = new Map();
    function proxyOf(el) { return el.classList.contains('jj-reveal-wipe') ? (el.parentElement || el) : el; }
    function hit(e, io) {
      if (!e.isIntersecting) return;
      (byProxy.get(e.target) || []).forEach(arrive);
      byProxy.delete(e.target);
      io.unobserve(e.target);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { hit(e, io); });
    }, { threshold: 0.2, rootMargin: '0px 0px -4% 0px' });
    var ioBig = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { hit(e, ioBig); });
    }, { threshold: 0.05 });
    els.forEach(function (el) {
      var proxy = proxyOf(el);
      var first = !byProxy.has(proxy);
      if (first) byProxy.set(proxy, []);
      byProxy.get(proxy).push(el);
      if (!first) return;
      var tall = el.classList.contains('jj-panel') || proxy.getBoundingClientRect().height > innerHeight * 0.7;
      (tall ? ioBig : io).observe(proxy);
    });
  }

  /* ---------------- sticky story parallax (6%, clamped to 88px) ---------------- */
  function initParallax() {
    var sticky = document.querySelector('.jj-story-sticky');
    var section = sticky && sticky.closest('.jj-story');
    if (!sticky || !section || reduce) return;
    var vis = false, dirty = false, rafOn = false;
    var gate = new IntersectionObserver(function (es) {
      es.forEach(function (e) { vis = e.isIntersecting; if (vis) kick(); });
    });
    gate.observe(section);
    function apply() {
      rafOn = false;
      if (!dirty || !vis) return;
      dirty = false;
      var r = section.getBoundingClientRect();
      var p = Math.min(1, Math.max(0, (innerHeight - r.top) / (innerHeight + r.height)));
      var travel = Math.min(0.06 * r.height, 88);
      sticky.style.setProperty('--parallax', ((p - 0.5) * travel).toFixed(1) + 'px');
      sticky.style.setProperty('--parallax-n', ((p - 0.5) * 2).toFixed(3));
    }
    function kick() { dirty = true; if (!rafOn) { rafOn = true; requestAnimationFrame(apply); } }
    bindScroll(kick);
    kick();
  }

  /* ---------------- pinned scroll-scrub hero ---------------- */
  function initHero() {
    var hero = document.querySelector('[data-jj-hero]');
    if (!hero) return;
    var frames = [].slice.call(hero.querySelectorAll('.jj-hero-frame'));
    var N = frames.length;
    if (!N) return;
    var wrap = hero.querySelector('.jj-hero-frames');
    var railLine = hero.querySelector('.jj-rail-line');
    var tickEl = hero.querySelector('.jj-rail-tick');
    var dots = [].slice.call(hero.querySelectorAll('.jj-hero-dots button'));
    var stone = hero.querySelector('.jj-hero-stone canvas.jade');
    var autoMs = parseInt(hero.getAttribute('data-jj-autoplay'), 10) || 4000;
    var shapes = frames.map(function (f) { return f.getAttribute('data-shape') || 'sphere'; });
    var JJ = window.JJ = window.JJ || {};

    function jade() { return (stone && JJ.jade && !JJ.jade.fallback) ? JJ.jade.get(stone) : null; }
    function shapeId(name) { return JJ.jade ? Math.max(0, JJ.jade.shapes.indexOf(name)) : 0; }
    var ctaSeries = hero.querySelector('[data-jj-hero-cta="series"]');
    var ctaAtelier = hero.querySelector('[data-jj-hero-cta="atelier"]');
    var photos = [].slice.call(hero.querySelectorAll('.jj-hero-photo'));
    var stoneBox = hero.querySelector('.jj-hero-stone');
    function setActive(k) {
      wrap.setAttribute('data-active', String(k));
      if (ctaSeries && ctaAtelier) { var at = shapes[k] === 'rough'; ctaSeries.hidden = at; ctaAtelier.hidden = !at; }
      frames.forEach(function (f, i) { f.classList.toggle('is-active', i === k); });
      dots.forEach(function (d, i) { d.setAttribute('aria-current', String(i === k)); });
      /* series photo layers: the active frame's product shot covers the jade canvas (none -> canvas) */
      var hasPhoto = false;
      photos.forEach(function (ph) {
        var on = ph.getAttribute('data-jj-hero-photo') === String(k);
        ph.classList.toggle('is-on', on);
        if (on) hasPhoto = true;
      });
      if (stoneBox) stoneBox.classList.toggle('has-photo', hasPhoto);
    }
    function morph(i, t, spin) {
      var j = jade();
      if (!j) { if (stone) stone.setAttribute('data-jade', shapes[Math.round(i + t)] || shapes[i]); return; }
      j.set({ a: shapeId(shapes[i]), b: shapeId(shapes[Math.min(i + 1, N - 1)]), mix: t * t * (3 - 2 * t), spin: spin });
    }
    JJ.hero = { frames: N, mode: reduce ? 'static' : 'auto' };
    /* release the load animation once played (a lingering animation pins the stone to a composited layer) */
    setTimeout(function () { hero.classList.add('is-settled'); }, 1900);

    /* -------- unified timer carousel (owner decision 2026-09-07: autoplay + prev/next arrows,
       scroll never changes the frame). reduced-motion: no autoplay, arrows/dots still navigate. -------- */
    var cur = 0, tw = null, interval = null, railW = 0;
    function placeTick() {
      if (tickEl && N > 1) tickEl.style.transform = 'translateX(' + ((cur / (N - 1)) * railW).toFixed(1) + 'px)';
    }
    function measure() { railW = railLine ? railLine.offsetWidth : 0; placeTick(); }
    function tween(from, to) {
      if (tw) cancelAnimationFrame(tw);
      var t0 = performance.now(), D = 900;
      (function step(now) {
        var t = Math.min(1, (now - t0) / D);
        morph(Math.min(from, to), from < to ? t : 1 - t, 0);
        if (t < 1) tw = requestAnimationFrame(step); else tw = null;
      })(t0);
    }
    function goTo(k) {
      k = (k + N) % N;
      if (k === cur) return;
      var lo = Math.min(cur, k), hi = Math.max(cur, k);
      if (!reduce && hi - lo === 1) tween(cur, k); else morph(k, 0, 0);
      cur = k;
      setActive(k);
      placeTick();
    }
    function startAuto() { stopAuto(); if (reduce) return; interval = setInterval(function () { goTo(cur + 1); }, autoMs); }
    function stopAuto() { if (interval) { clearInterval(interval); interval = null; } }
    dots.forEach(function (d, i) { d.addEventListener('click', function () { goTo(i); startAuto(); }); });
    var prevBtn = hero.querySelector('[data-jj-hero-prev]');
    var nextBtn = hero.querySelector('[data-jj-hero-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(cur - 1); startAuto(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(cur + 1); startAuto(); });
    window.addEventListener('resize', measure, { passive: true });
    hero.classList.add(reduce ? 'is-static' : 'is-auto');
    setActive(0);
    morph(0, 0, 0);
    measure();
    startAuto();
  }

  function init() { initReveals(); initParallax(); initHero(); }
  /* 2026-10-17: while the full-screen opening (snippets/jj-intro, html.jj-intro-on) plays, hold the page reveals
     until it leaves (`jj:intro-done`) so the page arrives as the layer lifts.
     2026-10-29 改版: the home-page brand prelude (snippets/jj-prelude, html.jj-prelude-on) follows the opening —
     the gate is re-read on `jj:intro-done`, and while the prelude is open the reveals wait for `jj:prelude-done`
     (fired as the visitor enters the shop, at the start of its 0.6 s exit fade). */
  function start() {
    if (html.classList.contains('jj-intro-on')) document.addEventListener('jj:intro-done', start, { once: true });
    else if (html.classList.contains('jj-prelude-on')) document.addEventListener('jj:prelude-done', init, { once: true });
    else init();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  /* 2026-10-31: theme editor - a re-rendered section brings fresh hidden reveals that the one-time observers never see;
     show them at their end state (storefront visitors never get this event) */
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('.jj-reveal,.jj-reveal-group,.jj-divider,.jj-panel,.jj-reveal-wipe').forEach(function (el) { el.classList.add('is-in'); });
    e.target.querySelectorAll('[data-jj-count]').forEach(function (el) { el.textContent = el.getAttribute('data-jj-count'); });
  });
})();
